import { useState, useEffect, useCallback, useRef } from "react";
import { InfoClient, SubscriptionClient } from "../sdk";
import { executeWithRetry, DefaultRetryConfigs } from "../sdk/utils/retry";
import { ConnectionState } from "../sdk/types/transport";
import { Position, PositionsConfig, transformPosition } from "../sdk/types/data";
import { MultiFeedPositionsData } from "../sdk/types/api";

// Re-export for backward compatibility
export type { Position, PositionsConfig };

/**
 * Hook for managing positions data
 * Uses WebSocket as primary source (via user_updates channel) with HTTP fallback
 * Maintains backward compatibility with polling approach
 */
export function usePositions(config: PositionsConfig) {
  const useWebSocket = config.useWebSocket !== false; // Default: true
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const infoClientRef = useRef<InfoClient | null>(null);
  const subscriptionClientRef = useRef<SubscriptionClient | null>(null);
  const refreshIntervalRef = useRef<number>(0);
  const isSubscribedRef = useRef<boolean>(false);
  const currentMarketIdRef = useRef<string | null>(null);
  const currentSymbolRef = useRef<string | null>(null);
  // Store config in ref to avoid dependency issues in callbacks
  const configRef = useRef(config);
  configRef.current = config;

  /**
   * Fetch positions from API with retry logic
   * Only updates state if data has actually changed to prevent unnecessary re-renders
   * Uses configRef to access latest config without causing dependency loops
   */
  const fetchPositions = useCallback(async () => {
    if (!infoClientRef.current) return;

    const currentConfig = configRef.current;
    // If walletAddress is not provided, we can't fetch positions
    // The API requires an address parameter
    // Don't set error state if address is simply not yet loaded (empty string)
    // Only set error if we've attempted to fetch and it failed
    if (
      !currentConfig.walletAddress ||
      currentConfig.walletAddress.trim() === ""
    ) {
      // Clear error and positions when address is not available
      setError("");
      setPositions([]);
      setLoading(false);
      return;
    }

    setError("");

    try {
      const data = await executeWithRetry(DefaultRetryConfigs.http, () =>
        infoClientRef.current!.getPositions(currentConfig.walletAddress!),
      );

      if (Array.isArray(data)) {
        const transformedPositions: Position[] = data.map(transformPosition);
        // Compare with current positions to avoid unnecessary state updates
        setPositions((prev) => {
          if (prev.length !== transformedPositions.length) {
            return transformedPositions;
          }
          // Deep comparison - only update if positions actually changed
          const prevStr = JSON.stringify(prev);
          const newStr = JSON.stringify(transformedPositions);
          return prevStr === newStr ? prev : transformedPositions;
        });
      } else {
        setPositions((prev) => (prev.length === 0 ? prev : []));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load positions");
      console.error("Failed to fetch positions after retries:", err);
    } finally {
      setLoading(false);
    }
  }, []); // transformPosition is now a pure function imported from data.ts

  /**
   * Handle position updates from WebSocket (multi-feed positions sub-channel)
   * Backend sends position data via multi-feed with typed MultiFeedPositionsData
   * Only updates state if data has actually changed to prevent unnecessary re-renders
   */
  const handleUserUpdates = useCallback(
    (data: MultiFeedPositionsData) => {
      // Multi-feed positions data structure: { market_id, address, symbol, ... }
      // Single position update - transform and update state
      if (data.market_id && data.symbol) {
        const transformedPosition = transformPosition(data);
        setPositions((prev) => {
          // Find existing position by positionId (preferred) or market_id (fallback)
          const existingIndex = prev.findIndex(
            (p) =>  p.positionId === transformedPosition.positionId,
          );
          if (existingIndex >= 0) {
            // Update existing position
            const updated = [...prev];
            updated[existingIndex] = transformedPosition;
            // Deep comparison
            const prevStr = JSON.stringify(prev);
            const newStr = JSON.stringify(updated);
            return prevStr === newStr ? prev : updated;
          } else {
            // Add new position
            return [...prev, transformedPosition];
          }
        });
      }
    },
    [], // transformPosition is now a pure function imported from data.ts
  );

  // Initialize clients and set up WebSocket or polling
  useEffect(() => {
    const infoClient = new InfoClient({
      baseURL: config.baseURL,
      sessionToken: config.authToken,
    });
    infoClientRef.current = infoClient;

    // Initial fetch from HTTP (always do this for fast initial load)
    fetchPositions();

    const subscriptionClient = config.subscriptionClient;

    if (useWebSocket) {
      // WebSocket mode: primary source
      subscriptionClientRef.current = subscriptionClient;

      // Subscribe function that can be called on initial connect or reconnection
      const subscribe = () => {
        // Check if ready for subscriptions (connected and authenticated if required)
        if (!subscriptionClient.isReadyForSubscriptions()) {
          if (subscriptionClient.isConnected() && !subscriptionClient.isReadyForSubscriptions()) {
            // Connected but not authenticated - wait a bit and retry
            const authState = subscriptionClient.getAuthState();
            if (authState === 'pending') {
              console.debug("[usePositions] Waiting for authentication to complete...");
              // Will retry on next state change
            } else if (authState === 'failed') {
              console.error("[usePositions] Authentication failed - cannot subscribe to positions");
              setError("Authentication failed - cannot subscribe to positions");
            }
          }
          return;
        }

        // Check if we need to re-subscribe (marketId or symbol changed, or not yet subscribed)
        const marketId = config.marketId ?? "0"; // Default to "0" for user-global
        const symbol = config.symbol ?? "USDC/USDC"; // Default symbol for user-global
        const needsResubscribe = 
          !isSubscribedRef.current ||
          currentMarketIdRef.current !== marketId ||
          currentSymbolRef.current !== symbol;

        if (needsResubscribe) {
          // Unsubscribe from previous subscription if it exists
          if (isSubscribedRef.current && subscriptionClientRef.current) {
            try {
              subscriptionClientRef.current.unsubscribeMultiFeedSubChannel(
                "positions",
                handleUserUpdates,
              );
            } catch (error) {
              console.warn("[usePositions] Error unsubscribing from previous positions subscription:", error);
            }
          }

          try {
            // Use multi-feed sub-channel for positions (same subscription as other user data)
            subscriptionClient.subscribeMultiFeedSubChannel(
              "positions",
              marketId,
              handleUserUpdates,
              { symbol },
            );
            isSubscribedRef.current = true;
            currentMarketIdRef.current = marketId;
            currentSymbolRef.current = symbol;
            setLoading(false);
            console.debug("[usePositions] Subscribed to positions:", {
              marketId,
              symbol,
            });
          } catch (error) {
            console.error("[usePositions] Failed to subscribe to positions:", error);
            setError(
              error instanceof Error
                ? error.message
                : "Failed to subscribe to positions"
            );
            isSubscribedRef.current = false;
            setLoading(false);
          }
        }
      };

      // Set initial connection state
      setIsConnected(subscriptionClient.isConnected());
      setLoading(false);

      // Try to subscribe immediately if client is ready
      if (subscriptionClient.isReadyForSubscriptions()) {
        subscribe();
      }

      // Monitor connection state changes and re-subscribe on reconnection
      // Use the public onStateChange method from SubscriptionClient
      const unsubscribeStateChange = subscriptionClient.onStateChange(
        (state: ConnectionState) => {
          console.debug("[usePositions] WebSocket state changed to:", state);
          setIsConnected(state === ConnectionState.Connected);
          if (state === ConnectionState.Connected) {
            // Always try to subscribe on connection (subscribe function handles re-subscription logic)
            subscribe();
            // Stop polling when WS is connected
            if (refreshIntervalRef.current !== null) {
              clearInterval(refreshIntervalRef.current);
              refreshIntervalRef.current = 0;
            }
          } else if (
            state === ConnectionState.Disconnected ||
            state === ConnectionState.Reconnecting
          ) {
            // Don't reset isSubscribedRef here - let subscribe() handle re-subscription
            // This allows us to track if we need to re-subscribe with new params
            // Fallback to polling when WS is disconnected
            if (config.refreshInterval && config.refreshInterval > 0) {
              if (refreshIntervalRef.current === null) {
                refreshIntervalRef.current = window.setInterval(() => {
                  fetchPositions();
                }, config.refreshInterval);
              }
            }
          }
        }
      );

      return () => {
        // Unsubscribe from state changes
        if (unsubscribeStateChange) {
          unsubscribeStateChange();
        }

        if (refreshIntervalRef.current !== null) {
          clearInterval(refreshIntervalRef.current);
          refreshIntervalRef.current = 0;
        }

        // Unsubscribe from positions sub-channel
        if (subscriptionClientRef.current && isSubscribedRef.current) {
          try {
            subscriptionClientRef.current.unsubscribeMultiFeedSubChannel(
              "positions",
              handleUserUpdates,
            );
          } catch (error) {
            console.warn("[usePositions] Error unsubscribing during cleanup:", error);
          }
        }

        // Reset subscription state
        isSubscribedRef.current = false;
        currentMarketIdRef.current = null;
        currentSymbolRef.current = null;
      };
    } else {
      // Polling mode: fallback or when useWebSocket is false
      if (config.refreshInterval && config.refreshInterval > 0) {
        refreshIntervalRef.current = window.setInterval(() => {
          fetchPositions();
        }, config.refreshInterval);
      }

      return () => {
        if (refreshIntervalRef.current !== null) {
          clearInterval(refreshIntervalRef.current);
          refreshIntervalRef.current = 0;
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    config.subscriptionClient,
    config.baseURL,
    config.authToken,
    config.walletAddress,
    config.refreshInterval,
    config.marketId,
    config.symbol,
    useWebSocket,
    // Note: fetchPositions and handleUserUpdates are intentionally NOT in dependencies
    // fetchPositions uses configRef to access latest config without causing dependency loops
    // handleUserUpdates is stable (only depends on transformPosition) and subscription is managed separately
    // The effect will re-run when walletAddress changes (included above), which is the desired behavior
  ]);

  return {
    positions,
    loading,
    error,
    isConnected,
    refresh: fetchPositions,
  };
}
